import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Youtube, CheckCircle2, AlertTriangle, RefreshCw, Key, ExternalLink,
  BookOpen, Copy, Info, Check, Tv, Unplug
} from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem("cortoviral_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const YouTubeConfigView = ({ onRefreshStatus }) => {
  const [status, setStatus] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [aiDisclosure, setAiDisclosure] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState(null);
  const [copiedUri, setCopiedUri] = useState(false);
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState("");

  const redirectUri = status?.redirect_uri || `${window.location.origin}/api/youtube/callback`;
  const connected = !!status?.token_health?.connected;
  const hasCreds = !!status?.has_client_credentials;

  const handleCopyUri = () => {
    navigator.clipboard.writeText(redirectUri);
    setCopiedUri(true);
    setTimeout(() => setCopiedUri(false), 2000);
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/youtube/status`, { params: { origin: window.location.origin }, headers: authHeaders(), withCredentials: true });
      setStatus(res.data);
      setAiDisclosure(res.data?.ai_disclosure ?? true);
      setChannels(res.data?.token_health?.available_channels || []);
      setSelectedChannelId(res.data?.token_health?.selected_channel_id || "");
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const onMsg = (ev) => {
      if (ev.data?.type === "YOUTUBE_CONNECTED_SUCCESS") {
        setConnecting(false);
        setMessage({ type: "success", text: ev.data.message || "Canal conectado correctamente." });
        fetchStatus();
        if (onRefreshStatus) onRefreshStatus();
      } else if (ev.data?.type === "YOUTUBE_CONNECTED_ERROR") {
        setConnecting(false);
        setMessage({ type: "error", text: ev.data.message || "Google rechazó la conexión." });
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [fetchStatus, onRefreshStatus]);

  const handleRefreshConnection = async () => {
    setRefreshing(true);
    setMessage(null);
    try {
      const chRes = await axios.get(`${API}/youtube/refresh-connection`, { headers: authHeaders(), withCredentials: true });
      setChannels(chRes.data.channels || []);
      setSelectedChannelId(chRes.data.selected_channel_id || "");
      await fetchStatus();
      setMessage(chRes.data.token_health?.connected
        ? { type: "success", text: `Conexión real verificada con YouTube: ${chRes.data.selected_channel_title}.` }
        : { type: "error", text: chRes.data.token_health?.message });
      if (onRefreshStatus) onRefreshStatus();
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Error verificando la conexión con YouTube." });
    } finally {
      setRefreshing(false);
    }
  };

  const handleSelectChannel = async (channel) => {
    try {
      await axios.post(`${API}/youtube/select-channel`, { channel_id: channel.id }, { headers: authHeaders(), withCredentials: true });
      setSelectedChannelId(channel.id);
      setMessage({ type: "success", text: `Canal de destino: ${channel.title}` });
      fetchStatus();
      if (onRefreshStatus) onRefreshStatus();
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Error cambiando de canal." });
    }
  };

  const handleConnectChannelExternal = async () => {
    setMessage(null);
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/youtube/auth-url`, {
        params: { origin: window.location.origin },
        headers: authHeaders(),
        withCredentials: true
      });
      const popup = window.open(res.data.auth_url, "cortoviral_google_oauth", "width=650,height=780,left=200,top=80,resizable=yes,scrollbars=yes");
      if (!popup) {
        window.location.href = res.data.auth_url;
        return;
      }
      setMessage({ type: "info", text: "Elige tu cuenta y canal en la ventana de Google. Al terminar, esta pantalla se actualizará sola." });
      const timer = setInterval(() => {
        if (popup.closed) {
          clearInterval(timer);
          setConnecting(false);
          fetchStatus();
        }
      }, 800);
    } catch (e) {
      setConnecting(false);
      setMessage({ type: "error", text: e.response?.data?.detail || "Error iniciando la conexión con Google." });
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/youtube/disconnect`, {}, { headers: authHeaders(), withCredentials: true });
      setMessage({ type: "info", text: "Canal desconectado. Puedes volver a conectar otro canal cuando quieras." });
      await fetchStatus();
      if (onRefreshStatus) onRefreshStatus();
    } catch (e) {
      setMessage({ type: "error", text: "Error desconectando el canal." });
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await axios.post(`${API}/youtube/config`,
        { client_id: clientId, client_secret: clientSecret, ai_disclosure: aiDisclosure },
        { headers: authHeaders(), withCredentials: true });
      setClientId("");
      setClientSecret("");
      setMessage({ type: "success", text: "Credenciales guardadas. Ahora pulsa 'Conectar Canal' para autorizar en Google." });
      await fetchStatus();
      if (onRefreshStatus) onRefreshStatus();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Error guardando configuración." });
    } finally {
      setSaving(false);
    }
  };

  const msgStyle = {
    success: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30",
    error: "bg-red-500/10 text-red-400 border border-red-500/30",
    info: "bg-sky-500/10 text-sky-300 border border-sky-500/30"
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-red-600/10 border border-red-500/30 flex items-center justify-center text-red-500">
              <Youtube className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                Estado de la Conexión YouTube
                {connected ? (
                  <span data-testid="youtube-status-badge" className="text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full font-mono flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> CONECTADO
                  </span>
                ) : (
                  <span data-testid="youtube-status-badge" className="text-xs bg-amber-500/10 text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded-full font-mono flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> NO CONECTADO
                  </span>
                )}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5" data-testid="youtube-active-channel">
                Canal activo de publicación: <strong className="text-red-400 font-bold">{status?.token_health?.selected_channel_title || "Ningún canal conectado"}</strong>
              </p>
              {!connected && status && (
                <p className="text-[11px] text-amber-300/80 mt-1">{status.token_health?.message}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {connected && (
              <>
                <Button data-testid="youtube-refresh-connection-btn" onClick={handleRefreshConnection} disabled={refreshing}
                  className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-xs flex items-center gap-1.5 border border-white/10 py-2.5 px-3.5">
                  <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin text-emerald-400" : ""}`} /> Verificar
                </Button>
                <Button data-testid="youtube-disconnect-btn" onClick={handleDisconnect}
                  className="bg-slate-800 hover:bg-red-900/40 text-slate-200 font-semibold text-xs flex items-center gap-1.5 border border-white/10 py-2.5 px-3.5">
                  <Unplug className="w-3.5 h-3.5" /> Desconectar
                </Button>
              </>
            )}
            <Button data-testid="youtube-connect-channel-btn" onClick={handleConnectChannelExternal} disabled={connecting || !hasCreds}
              title={!hasCreds ? "Guarda primero tus credenciales de Google Cloud" : ""}
              className="bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white font-bold text-xs flex items-center gap-2 py-2.5 px-4 shadow-lg shadow-red-900/30">
              <ExternalLink className={`w-3.5 h-3.5 ${connecting ? "animate-pulse" : ""}`} />
              {connected ? "Conectar otro canal" : "Conectar Canal"}
            </Button>
          </div>
        </div>

        {message && (
          <div data-testid="youtube-message" className={`mt-4 p-3 rounded-lg text-xs flex items-center gap-2 ${msgStyle[message.type]}`}>
            <Info className="w-4 h-4 shrink-0" />
            <span>{message.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
          <div className="p-4 rounded-xl bg-[#1A1D2B] border border-white/5">
            <span className="text-xs text-slate-400 block mb-1">Cuota Diaria Shorts</span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-mono font-black text-white" data-testid="youtube-quota-used">{status?.daily_quota?.used ?? 0}</span>
              <span className="text-xs font-mono text-slate-500">/ {status?.daily_quota?.max ?? 5} publicados hoy</span>
            </div>
          </div>
          <div className="p-4 rounded-xl bg-[#1A1D2B] border border-white/5">
            <span className="text-xs text-slate-400 block mb-1">Divulgación Contenido IA</span>
            <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded inline-block mt-1">
              {aiDisclosure ? "PROTECCIÓN CANAL ACTIVA (ON)" : "DESACTIVADO"}
            </span>
          </div>
          <div className="p-4 rounded-xl bg-[#1A1D2B] border border-white/5">
            <span className="text-xs text-slate-400 block mb-1">Credenciales Google Cloud</span>
            <span className="text-xs font-mono text-slate-300 block mt-1 truncate" data-testid="youtube-client-id-masked">
              {hasCreds ? status.client_id_masked : "Sin guardar"}
            </span>
          </div>
        </div>
      </div>

      {connected && (
        <div data-testid="channel-selector-container" className="bg-[#12141D] border border-red-500/30 rounded-2xl p-6 shadow-xl space-y-5">
          <div>
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <Tv className="w-5 h-5 text-red-500" /> Canal de YouTube autorizado
            </h3>
            <p className="text-xs text-slate-400">
              Estos son los canales reales de la cuenta de Google que autorizaste. Para usar otro canal (p. ej. de marca), pulsa "Conectar otro canal" y elígelo en la pantalla de Google.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {channels.map((ch) => {
              const isSelected = ch.id === selectedChannelId;
              return (
                <div key={ch.id} data-testid={`channel-option-${ch.id}`} onClick={() => handleSelectChannel(ch)}
                  className={`p-4 rounded-xl border cursor-pointer transition-colors flex flex-col justify-between gap-3 ${isSelected ? "bg-red-600/15 border-red-500 shadow-md shadow-red-900/30" : "bg-[#1A1D2B] border-white/5 hover:border-white/20"}`}>
                  <div className="flex items-start gap-3">
                    {ch.thumbnail && <img src={ch.thumbnail} alt={ch.title} className="w-10 h-10 rounded-full object-cover border border-white/10 shrink-0" />}
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-white truncate">{ch.title}</h4>
                      <span className="text-[10px] text-slate-400 font-mono block">{ch.handle || ch.subscribers}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-white/5 text-xs">
                    <a href={`https://youtube.com/channel/${ch.id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                      className="font-mono text-[10px] text-slate-500 hover:text-white truncate max-w-[130px] underline underline-offset-2">
                      Ver canal
                    </a>
                    {isSelected ? (
                      <span className="text-[11px] font-bold text-emerald-400 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> CANAL ACTIVO</span>
                    ) : (
                      <span className="text-[11px] font-semibold text-slate-400 hover:text-white">Seleccionar</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 shadow-xl">
        <h3 className="text-base font-bold text-white mb-2 flex items-center gap-2">
          <Key className="w-4 h-4 text-amber-400" /> Credenciales Google Cloud OAuth2
        </h3>
        <p className="text-xs text-slate-400 mb-4">
          Necesarias para que CortoViral suba los videos a tu canal con tu propia cuota de la API de YouTube. Se guardan cifradas en tu cuenta.
        </p>

        <div data-testid="google-oauth-guide-box" className="mb-6 bg-[#161926] border border-red-500/30 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wider text-red-400 flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Miniguía Paso a Paso para Conectar tu Canal
            </h4>
            <a href="https://console.cloud.google.com/apis/dashboard" target="_blank" rel="noopener noreferrer"
              className="text-[11px] font-semibold text-slate-300 hover:text-white flex items-center gap-1 underline underline-offset-2">
              Abrir Google Cloud Console <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <ol className="list-decimal list-inside text-xs text-slate-300 space-y-2 leading-relaxed">
            <li><strong>Crear o seleccionar proyecto</strong> en Google Cloud Console.</li>
            <li><strong>Habilitar API:</strong> <em>APIs y Servicios &gt; Biblioteca</em>, busca <strong>YouTube Data API v3</strong> y pulsa <strong>Habilitar</strong>.</li>
            <li><strong>Pantalla de consentimiento OAuth:</strong> tipo <strong>Externo</strong>. En <em>Público / Usuarios de prueba</em> añade el correo de Google dueño de tu canal (si no, Google mostrará "access_denied").</li>
            <li><strong>Ámbitos (scopes):</strong> añade <code className="text-amber-300">youtube.upload</code> y <code className="text-amber-300">youtube.readonly</code>.</li>
            <li>
              <strong>Crear credenciales:</strong> <em>Credenciales &gt; Crear credenciales &gt; ID de cliente de OAuth</em>, tipo <strong>Aplicación web</strong>. En <strong>URIs de redireccionamiento autorizados</strong> pega EXACTAMENTE:
              <div className="mt-1 flex items-center gap-2 bg-black/50 border border-white/10 rounded p-1.5 font-mono text-[11px] text-amber-300 select-all">
                <span data-testid="redirect-uri-text" className="truncate">{redirectUri}</span>
                <button type="button" data-testid="copy-redirect-uri-btn" onClick={handleCopyUri}
                  className="ml-auto text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-200 px-2 py-0.5 rounded border border-white/10 flex items-center gap-1 shrink-0">
                  {copiedUri ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                  {copiedUri ? "Copiado" : "Copiar"}
                </button>
              </div>
            </li>
            <li><strong>Pegar claves:</strong> copia el <strong>ID de cliente</strong> y el <strong>Secreto</strong> abajo, guarda y pulsa <strong>Conectar Canal</strong>. Google te dejará elegir la cuenta y el canal (incluidos canales de marca).</li>
          </ol>
        </div>

        <form onSubmit={handleSaveConfig} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1">Google Client ID</label>
            <input data-testid="youtube-client-id-input" type="text" value={clientId} onChange={(e) => setClientId(e.target.value)}
              placeholder={hasCreds ? status.client_id_masked : "xxxxxxxxxxxx.apps.googleusercontent.com"}
              className="w-full bg-[#1A1D2B] border border-white/10 rounded-lg px-3.5 py-2.5 text-xs font-mono text-white focus:ring-2 focus:ring-red-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1">Google Client Secret</label>
            <input data-testid="youtube-client-secret-input" type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)}
              placeholder={hasCreds ? "•••••••••••••••• (guardado)" : "GOCSPX-xxxxxxxxxxxxxxxx"}
              className="w-full bg-[#1A1D2B] border border-white/10 rounded-lg px-3.5 py-2.5 text-xs font-mono text-white focus:ring-2 focus:ring-red-500 focus:outline-none" />
          </div>
          <div className="p-3 bg-slate-900/60 border border-white/5 rounded-xl space-y-2">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input data-testid="youtube-ai-disclosure-checkbox" type="checkbox" checked={aiDisclosure} onChange={(e) => setAiDisclosure(e.target.checked)}
                className="w-4 h-4 rounded text-red-600 bg-slate-950 border-white/20 focus:ring-red-500" />
              <span className="text-xs font-semibold text-white">Marcar automáticamente como "Contenido alterado o sintético con IA" (Protege el canal)</span>
            </label>
          </div>
          <Button data-testid="save-youtube-settings-btn" type="submit" disabled={saving}
            className="bg-slate-800 hover:bg-slate-700 text-white font-semibold text-xs px-5 py-2.5 rounded-lg border border-white/10">
            {saving ? "Guardando..." : "Guardar Credenciales"}
          </Button>
        </form>
      </div>
    </div>
  );
};
